import React from 'react';
import {GenericErrorMsg} from '~/entities/dialogs';
import {handleAuthDataReceived, SignupSetupPasswordErrorCode, useSignupSetupPasswordMutation} from '~/entities/user';
import {SetPasswordForm} from '~/processes/onboarding/ui/shared/SetPasswordForm';
import {appSessionStorage} from '~/shared/persistence';
import {useAppDispatch} from '~/shared/store';
import {hashPassword} from '~/shared/utils/hashPassword';

type SetPasswordProps = {
    uuid: string;
    onPasswordSet: (password: string) => void;
    onSessionExpired: () => void;
};

export function SetPassword({uuid, onPasswordSet, onSessionExpired}: SetPasswordProps) {
    const dispatch = useAppDispatch();

    const handleError = (e: unknown) => {
        dispatch(GenericErrorMsg.open());
        throw e;
    };

    const {mutateAsync: setupPassword, isPending} = useSignupSetupPasswordMutation();

    const handleSubmit = async (password: string) => {
        const {value, problem} = await setupPassword({password, verificationSession: uuid}).catch(handleError);
        if (problem) {
            switch (problem.error_code) {
                case SignupSetupPasswordErrorCode.sessionNotFound:
                case SignupSetupPasswordErrorCode.sessionNotVerified: {
                    onSessionExpired();
                    return;
                }
                case SignupSetupPasswordErrorCode.validationFailed: {
                    return problem.issues;
                }
                default: {
                    dispatch(GenericErrorMsg.open());
                    return;
                }
            }
        }

        const hPass = hashPassword(password, value.user.username);
        if (hPass) {
            appSessionStorage.hPass.set(hPass);
        }
        dispatch(handleAuthDataReceived(value));
        onPasswordSet(password);
    };

    return <SetPasswordForm onSubmit={handleSubmit} isPending={isPending} />;
}
